import { useCallback, useEffect, useMemo } from 'react';

import { GrowthBook, GrowthBookProvider } from '@growthbook/growthbook-react';

import useAccount from 'hooks/useAccount';
import useCurrentOrganization from 'hooks/useCurrentOrganization';

import { TEST_ID } from './constants';
import type { FunctionComponent, Props } from './types';

const FeatureProvider: FunctionComponent<Props> = ({ children, testId = TEST_ID }) => {
    const growthbookInstance = useMemo(
        () =>
            new GrowthBook({
                apiHost: import.meta.env.VITE_GROWTHBOOK_API_HOST,
                clientKey: import.meta.env.VITE_GROWTHBOOK_CLIENT_KEY,
                enableDevMode: import.meta.env.DEV,
                subscribeToChanges: true
            }),
        []
    );

    const { user: currentUser } = useAccount();
    const { currentOrganization } = useCurrentOrganization();

    const loadFeatures = useCallback(
        async ({
            organizationCreatedAt,
            organizationId,
            userId
        }: {
            organizationCreatedAt?: Date;
            organizationId?: string | null;
            userId?: string | null;
        }) => {
            const growthbookAttributes = {
                id: userId,
                organizationCreatedAt: organizationCreatedAt,
                organizationId: organizationId
            };

            growthbookInstance.setAttributes(growthbookAttributes);

            await growthbookInstance.loadFeatures();
        },
        [growthbookInstance]
    );

    useEffect(() => {
        if (!currentOrganization) {
            return;
        }

        if (!currentUser) {
            return;
        }

        loadFeatures({
            organizationCreatedAt: currentOrganization?.createdAt,
            organizationId: currentOrganization?.id,
            userId: currentUser?.id
        });

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentOrganization, currentUser]);

    if (!currentOrganization) {
        return children;
    }

    return (
        <GrowthBookProvider data-testid={testId} growthbook={growthbookInstance}>
            {children}
        </GrowthBookProvider>
    );
};

export default FeatureProvider;
